import { DateSelector, TimeSelector } from "@hudson/ui/components/FormFields";
import { useFormContext } from "@hudson/ui/components/ReactHookForm";
import classNames from "classnames";
import { format, parse } from "date-fns";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import styles from "./DateTimeSelector.module.css";

export interface DateTimeSelectorProps {
  className?: string;
  dateFormat: string;
  dateLabel?: string;
  fieldName: string;
  hideTime?: boolean;
  loading?: boolean;
  timeDefault?: string;
  timeFormat: string;
  timeLabel?: string;
}

export function DateTimeSelector({
  className,
  dateFormat,
  dateLabel,
  fieldName,
  hideTime,
  loading = false,
  timeDefault,
  timeFormat,
  timeLabel,
}: DateTimeSelectorProps): JSX.Element {
  // Hook input into form
  const { formState, getValues, register, setValue, unregister } = useFormContext();
  useEffect(() => {
    register(fieldName, { required: true });
    return () => {
      unregister(fieldName);
    };
  }, [fieldName, register, unregister]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = getValues(fieldName);
  const [time, setTime] = useState<string | null>(value ? format(value, "H:mm") : null);
  const [date, setDate] = useState<Date | null>(value || null);

  // Set new value if date or time change.
  useEffect(() => {
    const inputTime = hideTime ? timeDefault : time;
    if (!date || !inputTime) {
      if (value !== undefined) {
        setValue(fieldName, undefined, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }
    const newDateTime = parse(inputTime, "H:mm", date);
    if (newDateTime.toString() != value?.toString()) {
      setValue(fieldName, newDateTime, { shouldDirty: true, shouldValidate: true });
    }
  }, [date, time, timeDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  // Error handling
  const fieldError = formState.errors?.[fieldName];
  let dateError, timeError;
  if (fieldError?.type == "required") {
    if (!date) dateError = true;
    if (!time) timeError = true;
  }

  // If the value prop changes update our internal state (also runs on mount).
  useEffect(() => {
    if (value) {
      try {
        setDate(value);
        setTime(format(value, "H:mm"));
      } catch (error) {
        console.error(error);
      }
    } else {
      setDate(null);
      setTime(null);
    }
  }, [value]);

  return (
    <motion.div layout className={`inline-flex items-start ${className ? className : ""}`}>
      <div className={hideTime ? "mr-0" : "mr-3"}>
        <div className="mb-1">
          <DateSelector
            className={classNames(
              styles.date,
              { [styles.error]: dateError },
              "block border-0 h-11 normal:h-12",
            )}
            dateFormat={dateFormat}
            loading={loading}
            onChange={setDate}
            value={date}
          />
        </div>
        <label className="block text-sm font-medium text-gray-700">{dateLabel}&nbsp;</label>
      </div>
      {!hideTime && (
        <div>
          <div
            className={classNames(
              styles.timeContainer,
              { [styles.error]: timeError },
              "mb-1 rounded-sm",
            )}
          >
            <TimeSelector
              className={classNames(styles.time, "normal:pt-3")}
              classes={{
                container: `h-11 normal:h-12`,
                icon: "text-md",
                hour: `text-xl border-0`,
                colon: "text-md",
                minute: `text-xl border-0`,
                ampm: `text-xl border-0`,
              }}
              loading={loading}
              onChange={setTime}
              timeFormat={timeFormat}
              value={time}
            />
          </div>
          <label className="block text-sm font-medium text-gray-700">{timeLabel}</label>
        </div>
      )}
    </motion.div>
  );
}
